import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Stack,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { RootState } from "@/app/store";
import { Payment } from "@/types/payment.types";
import { exportService } from "@/utils/export.sevice";

export const ExportPaymentsButton: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const { filters, payments } = useSelector(
    (state: RootState) => state.payments
  );

  const hasFilters =
    filters.status ||
    filters.userId ||
    filters.userName ||
    filters.paymentItem ||
    filters.dateRange.start ||
    filters.dateRange.end;

  const calculateTotalAmount = (paymentsList: Payment[]): number => {
    return paymentsList.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getFiltersDescription = (): string => {
    const activeFilters: string[] = [];
    
    if (filters.status) activeFilters.push(`Estado: ${filters.status}`);
    if (filters.userName) activeFilters.push(`Usuario: ${filters.userName}`);
    if (filters.paymentItem) activeFilters.push(`Concepto: ${filters.paymentItem}`);
    
    return activeFilters.length > 0 ? activeFilters.join(', ') : 'Ninguno';
  };

  const getDateRangeDescription = (): string => {
    if (filters.dateRange.start && filters.dateRange.end) {
      return `${new Date(filters.dateRange.start).toLocaleDateString('es-CO')} - ${new Date(filters.dateRange.end).toLocaleDateString('es-CO')}`;
    }
    if (filters.dateRange.start) {
      return `Desde ${new Date(filters.dateRange.start).toLocaleDateString('es-CO')}`;
    }
    if (filters.dateRange.end) {
      return `Hasta ${new Date(filters.dateRange.end).toLocaleDateString('es-CO')}`;
    }
    return 'Todas';
  };

  const handleExportCurrentPage = async () => {
    try {
      setIsExporting(true);
      setExportError(null);
      debugger;
      const totalAmount = calculateTotalAmount(payments);
      const metadata = {
        totalAmount,
        filters: getFiltersDescription(),
        dateRange: getDateRangeDescription(),
      };

      exportService.exportToExcelWithMetadata(
        payments,
        metadata,
        { scope: 'current-page' }
      );
      debugger;
      setShowModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      setExportError(
        error instanceof Error 
          ? error.message 
          : "Error al exportar los datos"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportAllFiltered = async () => {
    try {
      setIsExporting(true);
      setExportError(null);
      const allFilteredPayments = payments; 
      debugger;
      const totalAmount = calculateTotalAmount(allFilteredPayments);
      const metadata = {
        totalAmount,
        filters: getFiltersDescription(),
        dateRange: getDateRangeDescription(),
      };

      exportService.exportToExcelWithMetadata(
        allFilteredPayments,
        metadata,
        { scope: 'all-filtered' }
      );
      debugger;
      setShowModal(false);
    } catch (error) {
      console.error("Export failed:", error);
      setExportError(
        error instanceof Error 
          ? error.message 
          : "Error al exportar los datos"
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setShowModal(true)}
        disabled={isExporting || payments.length === 0}
        startIcon={
          isExporting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <DownloadIcon sx={{ fontSize: { xs: "1rem", md: "1.1rem" } }} />
          )
        }
        sx={{
          height: { xs: "40px", sm: "38px", md: "36px" },
          fontSize: { xs: "0.875rem", md: "0.875rem" },
          fontWeight: 500,
          letterSpacing: "0.5px",
          backgroundColor: "primary.500",
          "&:hover": {
            backgroundColor: "primary.dark",
          },
          "&:disabled": {
            backgroundColor: "#9ca3af",
          },
        }}
      >
        {isExporting ? "Exportando..." : "EXPORTAR"}
      </Button>

      <Dialog
        open={showModal}
        onClose={() => !isExporting && setShowModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Exportar Reporte
          </Typography>
          <IconButton
            onClick={() => setShowModal(false)}
            disabled={isExporting}
            size="small"
            sx={{
              color: "grey.500",
              "&:hover": {
                backgroundColor: "grey.100",
                color: "grey.700",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2.5 }}
          >
            Selecciona qué datos deseas exportar a Excel
          </Typography>

          <Stack spacing={1.5}>
            {/* Current Page Export Option */}
            <Paper
              elevation={0}
              sx={{
                border: "2px solid",
                borderColor: "grey.300",
                borderRadius: 1,
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "secondary.main",
                  backgroundColor: "secondary.100",
                  transform: "translateX(4px)",
                },
                cursor: isExporting ? "not-allowed" : "pointer",
                opacity: isExporting ? 0.6 : 1,
              }}
              onClick={!isExporting ? handleExportCurrentPage : undefined}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                }}
              >
                <DescriptionIcon
                  sx={{ fontSize: 28, color: "grey.600" }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    Página actual
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {payments.length} registros visibles
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* All Data Export Option - Always visible */}
            <Paper
              elevation={0}
              sx={{
                border: "2px solid",
                borderColor: "primary.500",
                borderRadius: 1,
                background: "linear-gradient(135deg, primary.100 0%, #ecfdf5 100%)",
                transition: "all 0.2s",
                "&:hover": {
                  transform: "translateX(4px)",
                  boxShadow: "0 4px 6px -1px rgba(16, 185, 129, 0.1)",
                },
                cursor: isExporting ? "not-allowed" : "pointer",
                opacity: isExporting ? 0.6 : 1,
              }}
              onClick={!isExporting ? handleExportAllFiltered : undefined}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 2,
                }}
              >
                <AssessmentIcon
                  sx={{ fontSize: 28, color: "primary.500" }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    fontWeight={600}
                    color="text.primary"
                    sx={{ mb: 0.5 }}
                  >
                    {hasFilters ? "Todos los filtrados" : "Todos los registros"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {hasFilters 
                      ? "Todos los registros con los filtros aplicados"
                      : "Exportar toda la base de datos de pagos"
                    }
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Stack>

          {/* Error Alert */}
          {exportError && (
            <Alert
              severity="error"
              onClose={() => setExportError(null)}
              sx={{ mt: 2 }}
            >
              {exportError}
            </Alert>
          )}

          {/* Loading State */}
          {isExporting && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mt: 2,
                p: 2,
                backgroundColor: "secondary.main",
                borderRadius: 1,
              }}
            >
              <CircularProgress size={20} sx={{ color: "primary.500" }} />
              <Typography variant="body2" color="text.secondary">
                Generando archivo Excel...
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};